import { AsyncThunkPayloadCreator } from '@reduxjs/toolkit';

/**
 * Wraps an async API call with standardized error handling for Redux async thunks.
 * Extracts error details from API response and returns them via rejectWithValue.
 *
 * @param apiCall - The API function to wrap
 * @param errorMessage - Optional custom error message prefix
 * @returns AsyncThunkPayloadCreator with error handling
 *
 * @example
 * export const createEvent = createAsyncThunk(
 *   actionTypes.CREATE_EVENT,
 *   withErrorHandling(
 *     (event: Event) => createEventApi(event),
 *     'Error creating event'
 *   ),
 * );
 */
export const withErrorHandling = <Returned, ThunkArg = void>(
  apiCall: (arg: ThunkArg) => Promise<Returned>,
  errorMessage = 'Operation failed',
): AsyncThunkPayloadCreator<Returned, ThunkArg> => {
  return async (arg, { rejectWithValue }) => {
    try {
      const data = await apiCall(arg);
      return data;
    } catch (error: any) {
      console.error(`${errorMessage}:`, error);

      // Extract error message from various possible locations in the error response
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        errorMessage;

      // Return structured error object so components can access detailed error info
      return rejectWithValue({
        message,
        details: error.response?.data?.details,
        statusCode: error.response?.status,
      });
    }
  };
};
